/**
 * AudioHeaderProbe — read sampleRate + channel count from an audio file's
 * HEADER, without decoding it.
 *
 * Why this exists
 * ───────────────
 * The common pattern for "what sample rate is this file?" is:
 *
 *     audioContext.decodeAudioData(await file.arrayBuffer())
 *
 * That decodes the ENTIRE file into a PCM AudioBuffer — for a 677 MB WAV that
 * is multi-GB of Float32 in memory, and it OOMs (`Array buffer allocation
 * failed`). Telemetry 2026-05-15: 7 of 9 `sr_detect_failed` on /audio-enhancer
 * were 58–677 MB files OOMing in exactly this step — purely to read one number
 * that sits in the first 50 bytes of the file.
 *
 * sampleRate and channel count live in the file HEADER. This module reads only
 * the first few KB and parses it. O(1) memory, instant, no OOM.
 *
 * Supported containers: WAV (RIFF), MP3 (MPEG audio frame), FLAC, AIFF/AIFC
 * natively, plus MP4/M4A/MOV/3GP and Matroska/WebM by delegating to
 * MediaAudioProbe when that module is on the page. OGG / unknown → returns null;
 * the caller should fall back to a safe default (the processing pipeline
 * re-derives the true rate from decoded chunks anyway).
 *
 * Why the delegation (2026-08-29): MP4 and Matroska are the containers a
 * BROWSER-UNSUPPORTED codec arrives in (E-AC-3 / AC-3 / DTS / TrueHD). For those,
 * "returns null, the caller decodes instead" is not a small fallback — it is a
 * 20-60 s FFmpeg-WASM round trip to learn a number that sits in `moov`. Measured
 * on /channel-converter with a user's E-AC-3 5.1 .m4a: 0.2 ms here against 55 s
 * to decode. MediaAudioProbe already walked those containers for the codec id;
 * only the layout fields were missing from it.
 *
 * Public API
 * ──────────
 *   await AudioHeaderProbe.probe(file) → { sampleRate, channels, format, bits?, float?,
 *                                          totalSamples?, durationSec?, kbps? } | null
 *     file:   a Blob / File.
 *     return: { sampleRate:Number, channels:Number, format:'wav'|'mp3'|'flac'|'aiff',
 *               bits?:Number, float?:Boolean, totalSamples?:Number, durationSec?:Number,
 *               kbps?:Number (MP3 only: Xing average over the audio bytes, else the CBR frame rate) }
 *             or null when the header could not be parsed. `bits`/`float` are only
 *             present for the lossless containers (WAV/FLAC); MP3 has no PCM word
 *             length so they are omitted (caller defaults to 24-bit).
 *
 *     `totalSamples`/`durationSec` (added 2026-08-09) come from a count the header
 *     ALREADY carries — FLAC STREAMINFO's 36-bit total-samples, WAV's 'data' chunk
 *     size ÷ block align, AIFF COMM's numSampleFrames. For MP3 (2026-08-29) it is
 *     the Xing/Info or VBRI frame count when the encoder wrote one, and the CBR
 *     identity bytes×8÷bitrate when it did not. Either can still come back 0 —
 *     a caller must treat 0 as "unknown", never as "empty file".
 *
 *     Why it matters: duration × rate × channels is the size of the Float32 buffer
 *     decodeAudioData is ABOUT to allocate. Knowing it beforehand is what lets a
 *     caller route a big file to the streaming FFmpeg lane instead of discovering
 *     the ceiling by hitting it — /convert's lite path measured its peak AFTER the
 *     decode that causes the peak, which is no gate at all (see convert-tool.js).
 *
 * Three separate header parsers already exist in the codebase (quality-module
 * WAV/FLAC bit-depth, audio-stream-pipeline readWavHeader, mp3-frame-trim
 * parseFrameHeader) — none reusable. This is the consolidated, reusable one
 * for the sampleRate/channels question.
 */
(function () {
    'use strict';

    // MPEG audio frame header tables (mirror mp3-frame-trim.js exactly).
    var MPEG_VERSIONS = ['2.5', null, '2', '1'];
    var LAYERS = [null, 'Layer3', 'Layer2', 'Layer1'];
    var BITRATE = {
        '1_Layer1':   [0, 32, 64, 96, 128, 160, 192, 224, 256, 288, 320, 352, 384, 416, 448, -1],
        '1_Layer2':   [0, 32, 48, 56,  64,  80,  96, 112, 128, 160, 192, 224, 256, 320, 384, -1],
        '1_Layer3':   [0, 32, 40, 48,  56,  64,  80,  96, 112, 128, 160, 192, 224, 256, 320, -1],
        '2_Layer1':   [0, 32, 48, 56,  64,  80,  96, 112, 128, 144, 160, 176, 192, 224, 256, -1],
        '2_Layer2':   [0,  8, 16, 24,  32,  40,  48,  56,  64,  80,  96, 112, 128, 144, 160, -1],
        '2_Layer3':   [0,  8, 16, 24,  32,  40,  48,  56,  64,  80,  96, 112, 128, 144, 160, -1],
        '2.5_Layer1': [0, 32, 48, 56,  64,  80,  96, 112, 128, 144, 160, 176, 192, 224, 256, -1],
        '2.5_Layer2': [0,  8, 16, 24,  32,  40,  48,  56,  64,  80,  96, 112, 128, 144, 160, -1],
        '2.5_Layer3': [0,  8, 16, 24,  32,  40,  48,  56,  64,  80,  96, 112, 128, 144, 160, -1],
    };
    var SAMPLE_RATE = {
        '1':   [44100, 48000, 32000, -1],
        '2':   [22050, 24000, 16000, -1],
        '2.5': [11025, 12000,  8000, -1],
    };
    var SAMPLES_PER_FRAME = {
        '1_Layer1': 384, '1_Layer2': 1152, '1_Layer3': 1152,
        '2_Layer1': 384, '2_Layer2': 1152, '2_Layer3': 576,
        '2.5_Layer1': 384, '2.5_Layer2': 1152, '2.5_Layer3': 576,
    };

    function readSlice(file, start, end) {
        return file.slice(start, end).arrayBuffer();
    }

    // ── WAV ────────────────────────────────────────────────────────────────
    // RIFF container: walk chunks from offset 12 to find 'fmt '. The fmt body
    // carries channels (offset+10, u16le) and sampleRate (offset+12, u32le).
    function parseWav(bytes, fileSize) {
        var dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
        if (bytes.length < 44) return null;
        // 'RIFF' .... 'WAVE'
        if (bytes[0] !== 0x52 || bytes[1] !== 0x49 || bytes[2] !== 0x46 || bytes[3] !== 0x46) return null;
        if (bytes[8] !== 0x57 || bytes[9] !== 0x41 || bytes[10] !== 0x56 || bytes[11] !== 0x45) return null;
        var off = 12;
        var res = null;
        while (off + 8 <= bytes.length) {
            var id = String.fromCharCode(bytes[off], bytes[off + 1], bytes[off + 2], bytes[off + 3]);
            var size = dv.getUint32(off + 4, true);
            if (id === 'fmt ') {
                if (off + 16 > bytes.length) return null;
                var fmtTag = dv.getUint16(off + 8, true);
                var channels = dv.getUint16(off + 10, true);
                var sampleRate = dv.getUint32(off + 12, true);
                // bitsPerSample lives at body offset 14 (= off+22). For WAVE_FORMAT_EXTENSIBLE
                // (0xFFFE) the *container* word length is there, but the meaningful precision
                // is wValidBitsPerSample (body offset 18 = off+26), and the real codec is the
                // sub-format GUID's first 2 bytes (body offset 24 = off+32): 0x0003 = IEEE float.
                var bits = (off + 24 <= bytes.length) ? dv.getUint16(off + 22, true) : 0;
                var isFloat = (fmtTag === 0x0003);
                if (fmtTag === 0xFFFE) {
                    if (off + 28 <= bytes.length) {
                        var valid = dv.getUint16(off + 26, true);
                        if (valid > 0) bits = valid;
                    }
                    if (off + 34 <= bytes.length) isFloat = (dv.getUint16(off + 32, true) === 0x0003);
                }
                if (sampleRate > 0 && channels > 0) {
                    res = { sampleRate: sampleRate, channels: channels, format: 'wav',
                            bits: (bits > 0 ? bits : 0), float: !!isFloat,
                            totalSamples: 0, durationSec: 0 };
                    // НЕ выходим: длительность живёт в заголовке чанка 'data',
                    // который у обычного WAV идёт сразу за 'fmt '. Досматриваем
                    // окно и, если нашли, отдаём кадры — это тот же принцип
                    // «читаем факт из заголовка, а не декодируем».
                    off += 8 + size + (size & 1);
                    continue;
                }
                return null;
            }
            if (id === 'data' && res) {
                var bytesPerFrame = res.channels * Math.max(1, Math.round((res.bits || 16) / 8));
                // Стримовый WAV пишет в поле размера 0 или 0xFFFFFFFF — такому
                // числу верить нельзя, лучше отдать 0 (вызывающий сам решит).
                if (size > 0 && bytesPerFrame > 0 && (!fileSize || size <= fileSize)) {
                    res.totalSamples = Math.floor(size / bytesPerFrame);
                    res.durationSec = res.totalSamples / res.sampleRate;
                }
                return res;
            }
            off += 8 + size + (size & 1); // chunks are word-aligned
        }
        return res;
    }

    // ── FLAC ───────────────────────────────────────────────────────────────
    // 'fLaC' + 4-byte metadata block header + STREAMINFO body. sampleRate is
    // a 20-bit field, channels a 3-bit field, packed at body bytes 10-12
    // (= file offset 18-20).
    function parseFlac(bytes) {
        if (bytes.length < 26) return null;
        if (bytes[0] !== 0x66 || bytes[1] !== 0x4C || bytes[2] !== 0x61 || bytes[3] !== 0x43) return null; // 'fLaC'
        var b = 18; // 4 ('fLaC') + 4 (block header) + 10 (into STREAMINFO body)
        var sampleRate = (bytes[b] << 12) | (bytes[b + 1] << 4) | (bytes[b + 2] >> 4);
        var channels = ((bytes[b + 2] >> 1) & 0x07) + 1;
        // bits-per-sample: the 5 bits straddling body bytes 12-13 (lowest bit of
        // bytes[b+2] + top 4 bits of bytes[b+3]), stored minus one.
        var bits = (((bytes[b + 2] & 0x01) << 4) | (bytes[b + 3] >> 4)) + 1;
        // total samples — 36-битное поле сразу за bits-per-sample: младшие 4 бита
        // байта 21 плюс байты 22-25. Старшую часть берём УМНОЖЕНИЕМ, а не сдвигом:
        // побитовые операции в JS 32-битные, и `x << 32` дал бы обратно x.
        var totalSamples = (bytes[b + 3] & 0x0F) * 4294967296 +
            (((bytes[b + 4] << 24) | (bytes[b + 5] << 16) | (bytes[b + 6] << 8) | bytes[b + 7]) >>> 0);
        if (sampleRate > 0 && channels > 0) {
            return { sampleRate: sampleRate, channels: channels, format: 'flac',
                     bits: (bits > 0 ? bits : 0), float: false,
                     totalSamples: totalSamples || 0,
                     durationSec: totalSamples ? totalSamples / sampleRate : 0 };
        }
        return null;
    }

    // ── AIFF / AIFC ────────────────────────────────────────────────────────
    // 'FORM' <size> 'AIFF'|'AIFC', then chunks; 'COMM' body: numChannels u16be,
    // numSampleFrames u32be, sampleSize u16be, sampleRate = IEEE-754 80-bit
    // extended float, big-endian. Chrome decodes AIFF natively but ONLY at the
    // device rate — without this parser a 24/96 AIFF silently became 44.1/48 k
    // on every output path (caught by a real user report, 2026-07-16).
    function parseAiff(bytes) {
        if (bytes.length < 30) return null;
        if (bytes[0] !== 0x46 || bytes[1] !== 0x4F || bytes[2] !== 0x52 || bytes[3] !== 0x4D) return null; // 'FORM'
        var kind = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]);
        if (kind !== 'AIFF' && kind !== 'AIFC') return null;
        var dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
        var off = 12;
        while (off + 8 <= bytes.length) {
            var id = String.fromCharCode(bytes[off], bytes[off + 1], bytes[off + 2], bytes[off + 3]);
            var size = dv.getUint32(off + 4, false);              // big-endian
            if (id === 'COMM') {
                if (off + 8 + 18 > bytes.length) return null;
                var channels = dv.getUint16(off + 8, false);
                var numFrames = dv.getUint32(off + 10, false);       // numSampleFrames
                var bits = dv.getUint16(off + 14, false);
                // 80-bit extended float: 1 sign + 15 exponent bits, 64-bit mantissa
                // with explicit integer bit. rate = mantissa × 2^(exp − 16383 − 63).
                var e = dv.getUint16(off + 16, false) & 0x7FFF;
                var hi = dv.getUint32(off + 18, false), lo = dv.getUint32(off + 22, false);
                var mant = hi * 4294967296 + lo;
                var sampleRate = (e && mant) ? Math.round(mant * Math.pow(2, e - 16383 - 63)) : 0;
                if (sampleRate > 0 && channels > 0) {
                    return { sampleRate: sampleRate, channels: channels, format: 'aiff',
                             bits: (bits > 0 ? bits : 0), float: false,
                             totalSamples: numFrames || 0,
                             durationSec: numFrames ? numFrames / sampleRate : 0 };
                }
                return null;
            }
            off += 8 + size + (size & 1);                         // chunks word-aligned
        }
        return null;
    }

    // ── MP3 ────────────────────────────────────────────────────────────────
    // Parse one MPEG audio frame header. Returns {sampleRate,channels,frameLength}
    // — frameLength is needed to confirm the NEXT frame (see parseMp3).
    function parseMpegFrame(bytes, offset) {
        if (offset + 4 > bytes.length) return null;
        var b1 = bytes[offset + 1], b2 = bytes[offset + 2], b3 = bytes[offset + 3];
        // Frame sync: byte0 == 0xFF, top 3 bits of byte1 set.
        if (bytes[offset] !== 0xFF || (b1 & 0xE0) !== 0xE0) return null;
        var version = MPEG_VERSIONS[(b1 >> 3) & 0x03];
        var layer = LAYERS[(b1 >> 1) & 0x03];
        if (!version || !layer) return null;
        var key = version + '_' + layer;
        var bitrate = (BITRATE[key] || [])[(b2 >> 4) & 0x0F];
        var sampleRate = (SAMPLE_RATE[version] || [])[(b2 >> 2) & 0x03];
        if (!bitrate || bitrate <= 0 || !sampleRate || sampleRate <= 0) return null;
        var padding = (b2 >> 1) & 0x01;
        var samples = SAMPLES_PER_FRAME[key];
        var frameLength = (layer === 'Layer1')
            ? (Math.floor(12 * bitrate * 1000 / sampleRate) + padding) * 4
            : Math.floor((samples / 8) * bitrate * 1000 / sampleRate) + padding;
        if (frameLength < 4) return null;
        // Channel mode: bits 6-7 of b3. 3 = mono.
        var channels = ((b3 >> 6) & 0x03) === 3 ? 1 : 2;
        return { sampleRate: sampleRate, channels: channels, frameLength: frameLength, format: 'mp3',
                 bitrate: bitrate, version: version, layer: layer, samplesPerFrame: samples };
    }

    // Find a frame, then CONFIRM it by requiring a second valid frame exactly
    // at offset+frameLength. A lone 4-byte sync coincidence is common in
    // arbitrary binary — OGG and M4A payloads contain byte runs that parse as
    // a "valid" single MPEG header. Two consecutive frames at the correct
    // stride is what actually distinguishes an MP3 stream, so the probe is
    // safe to use as a format sniffer (caught a real OGG/M4A false-positive
    // 2026-05-16).
    function parseMp3(bytes, audioBytes) {
        var limit = bytes.length - 4;
        for (var i = 0; i <= limit; i++) {
            if (bytes[i] !== 0xFF || (bytes[i + 1] & 0xE0) !== 0xE0) continue;
            var f1 = parseMpegFrame(bytes, i);
            if (!f1) continue;
            var f2 = parseMpegFrame(bytes, i + f1.frameLength);
            if (!f2) continue;
            var out = { sampleRate: f1.sampleRate, channels: f1.channels, format: 'mp3' };
            var frames = xingFrames(bytes, i, f1);
            var dur = 0;
            if (frames > 0) {
                dur = frames * f1.samplesPerFrame / f1.sampleRate;
            } else if (audioBytes > 0 && f1.bitrate > 0) {
                // No VBR header: assume CBR, which is the identity the header
                // already states. A VBR file without a Xing tag is the one case
                // this can be wrong about, and it is wrong by the ratio of its
                // average bitrate to its first frame's — a bounded error, and far
                // better than the 0 that used to force a full decode.
                dur = audioBytes * 8 / (f1.bitrate * 1000);
            }
            if (dur > 0 && isFinite(dur)) {
                out.durationSec = dur;
                out.totalSamples = Math.round(dur * f1.sampleRate);
            }
            /* Битрейт звука, кбит/с: при заголовке Xing/Info — средний по байтам
               ЗВУКА (без ID3 и обложки), иначе — битрейт кадра (CBR). Средний в
               пределах 3 % от битрейта кадра — это CBR с заголовком Info (LAME):
               берётся число кадра, а не «321». Мгновенно и без движка: проба
               FFmpeg на холодной странице отвечала позже, чем человек жал
               «Экспортировать» (/add-music-to-video, 08.10.2026). */
            var avgKbps = (frames > 0 && dur > 0 && audioBytes > 0) ? audioBytes * 8 / dur / 1000 : 0;
            out.kbps = (avgKbps > 0 && Math.abs(avgKbps - f1.bitrate) > f1.bitrate * 0.03) ? Math.round(avgKbps) : f1.bitrate;
            return out;
        }
        return null;
    }

    /* Xing / Info (LAME, at the side-info offset) or VBRI (Fraunhofer, at +32)
       inside the FIRST frame. Returns the frame count, or 0 when neither is
       present. The side-info offset depends on version + channel mode, which is
       why it is derived rather than guessed. */
    function xingFrames(bytes, o, f) {
        var side = (f.version === '1') ? (f.channels === 1 ? 17 : 32)
                                       : (f.channels === 1 ?  9 : 17);
        var q = o + 4 + side;
        if (q + 12 <= bytes.length) {
            var tag = String.fromCharCode(bytes[q], bytes[q + 1], bytes[q + 2], bytes[q + 3]);
            if (tag === 'Xing' || tag === 'Info') {
                var flags = be32(bytes, q + 4);
                if (flags & 0x0001) return be32(bytes, q + 8);
                return 0;
            }
        }
        var v = o + 4 + 32;
        if (v + 26 <= bytes.length &&
            bytes[v] === 0x56 && bytes[v + 1] === 0x42 && bytes[v + 2] === 0x52 && bytes[v + 3] === 0x49) {
            return be32(bytes, v + 14);
        }
        return 0;
    }
    function be32(b, o) { return ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0; }

    // ID3v2 tag size — syncsafe 28-bit integer at bytes 6-9. Used to skip the
    // tag (which can be hundreds of KB with embedded art) and reach audio.
    function id3v2Length(bytes) {
        if (bytes.length < 10) return 0;
        if (bytes[0] !== 0x49 || bytes[1] !== 0x44 || bytes[2] !== 0x33) return 0; // 'ID3'
        var size = (bytes[6] & 0x7F) << 21 | (bytes[7] & 0x7F) << 14 |
                   (bytes[8] & 0x7F) << 7  | (bytes[9] & 0x7F);
        return 10 + size;
    }

    /* ── exactMp3Duration ───────────────────────────────────────────────
     * Count the frames. The only exact answer for an MP3 whose encoder wrote
     * no Xing/VBRI header — and the ONE case every other method gets wrong the
     * same way, because they all extrapolate the first frame's bitrate over the
     * file: measured 2026-09-06 on a VBR clip of true length 43.651 s,
     * `<audio>.duration` said 39.738, ffprobe said 39.738, and the CBR identity
     * in parseMp3 above says 39.7 too. A slideshow built on that number is
     * 3.9 s short and cuts the end of the song off — «в конце песню обрезал
     * чуток, она не закончилась» (Tatiana Zaw, 06.09).
     *
     * Cost: one read of the file and a 4-byte look at each frame header —
     * ~9000 frames for four minutes of music, milliseconds, no decode. That is
     * the whole reason not to reach for decodeAudioData, which would allocate
     * ~10 MB per minute of stereo audio just to learn a number.
     *
     * Returns seconds, or 0 when this is not an MP3 / nothing parsed. 0 means
     * UNKNOWN — never "empty".
     */
    async function exactMp3Duration(file, opts) {
        if (!file || typeof file.arrayBuffer !== 'function') return 0;
        var maxBytes = (opts && opts.maxBytes) || 256 * 1024 * 1024;
        if (file.size > maxBytes) return 0;
        var bytes;
        try { bytes = new Uint8Array(await file.arrayBuffer()); }
        catch (_) { return 0; }
        var i = id3v2Length(bytes);
        var n = bytes.length;
        var seconds = 0, frames = 0, resyncs = 0;
        // Confirm the stream before trusting it, exactly as parseMp3 does: a
        // lone 4-byte sync coincidence is common in arbitrary binary.
        while (i < n - 4) {
            var f = parseMpegFrame(bytes, i);
            if (!f) {
                i++; resyncs++;
                // A file that never lines up is not an MP3 (or is damaged past
                // use). Give up rather than crawl a whole album byte by byte.
                if (resyncs > 1 << 20) return 0;
                continue;
            }
            if (frames === 0 && !parseMpegFrame(bytes, i + f.frameLength)) { i++; resyncs++; continue; }
            seconds += f.samplesPerFrame / f.sampleRate;
            frames++;
            i += f.frameLength;
        }
        return frames > 0 ? seconds : 0;
    }

    async function probe(file) {
        if (!file || typeof file.slice !== 'function') return null;
        try {
            var head = new Uint8Array(await readSlice(file, 0, Math.min(file.size, 65536)));
            if (head.length < 12) return null;

            // WAV
            if (head[0] === 0x52 && head[1] === 0x49 && head[2] === 0x46 && head[3] === 0x46) {
                return parseWav(head, file.size);
            }
            // FLAC
            if (head[0] === 0x66 && head[1] === 0x4C && head[2] === 0x61 && head[3] === 0x43) {
                return parseFlac(head);
            }
            // AIFF / AIFC ('FORM' container)
            if (head[0] === 0x46 && head[1] === 0x4F && head[2] === 0x52 && head[3] === 0x4D) {
                return parseAiff(head);
            }
            // Known non-MP3 containers — bail explicitly so the MP3 frame scan
            // below never gets a chance to false-match their binary payload.
            //   OGG  : 'OggS' at 0
            if (head[0] === 0x4F && head[1] === 0x67 && head[2] === 0x67 && head[3] === 0x53) return null;
            //   MP4/M4A/MOV/3GP : 'ftyp' box at bytes 4-7
            //   Matroska/WebM   : EBML magic 1A 45 DF A3
            if ((head[4] === 0x66 && head[5] === 0x74 && head[6] === 0x79 && head[7] === 0x70)
             || (head[0] === 0x1A && head[1] === 0x45 && head[2] === 0xDF && head[3] === 0xA3)) {
                return await probeContainer(file);
            }
            // MP3 — possibly behind an ID3v2 tag.
            var id3Len = id3v2Length(head);
            // Bytes that are actually audio: total minus the ID3v2 tag in front
            // and an ID3v1 trailer if there is one. Feeding the whole file size to
            // the CBR identity overstates the duration of a file with cover art.
            var tail = new Uint8Array(await readSlice(file, Math.max(0, file.size - 128), file.size));
            var id3v1 = (tail.length === 128 && tail[0] === 0x54 && tail[1] === 0x41 && tail[2] === 0x47) ? 128 : 0;
            var audioBytes = Math.max(0, file.size - id3Len - id3v1);
            if (id3Len > 0) {
                // First audio frame sits past the ID3v2 tag. If that's beyond
                // our 64 KB head slice, read a fresh 8 KB window at the tag end.
                if (id3Len + 4 <= head.length) {
                    var r = parseMp3(head.subarray(id3Len), audioBytes);
                    if (r) return r;
                }
                var win = new Uint8Array(await readSlice(file, id3Len, Math.min(file.size, id3Len + 8192)));
                return parseMp3(win, audioBytes);
            }
            // Bare MP3 (no ID3v2) or ID3-less stream.
            if (head[0] === 0xFF && (head[1] & 0xE0) === 0xE0) {
                return parseMp3(head, audioBytes);
            }
            // A frame may still appear after non-ID3 junk — last attempt.
            var scan = parseMp3(head, audioBytes);
            if (scan) return scan;

            return null; // OGG / M4A / unknown — caller falls back to a default
        } catch (e) {
            return null;
        }
    }

    /* MP4 / Matroska go through MediaAudioProbe, which owns those box walks.
       A soft dependency on purpose: when that module is not on the page this
       returns null and the caller behaves exactly as it did before. `0` from
       there means "the header did not say" and must NOT be published as a real
       channel count — a caller reading 0 as mono would turn a missing field
       into a wrong answer. */
    async function probeContainer(file) {
        var M = window.MediaAudioProbe;
        if (!M || typeof M.probe !== 'function') return null;
        var r = await M.probe(file);
        if (!r || (!r.channels && !r.sampleRate)) return null;
        var out = {
            sampleRate: r.sampleRate || 0,
            channels: r.channels || 0,
            format: r.source === 'mkv' ? 'matroska' : 'mp4',
            codec: (r.codecs && r.codecs[0]) || '',
        };
        if (r.bits) out.bits = r.bits;
        if (r.durationSec > 0) {
            out.durationSec = r.durationSec;
            if (out.sampleRate > 0) out.totalSamples = Math.round(r.durationSec * out.sampleRate);
        }
        return out;
    }

    window.AudioHeaderProbe = {
        probe: probe,
        exactMp3Duration: exactMp3Duration,
        // Exposed for unit tests — pure, synchronous parsers over a Uint8Array.
        _parseWav: parseWav,
        _parseFlac: parseFlac,
        _parseAiff: parseAiff,
        _parseMpegFrame: parseMpegFrame,
        _parseMp3: parseMp3,
        _xingFrames: xingFrames,
        _id3v2Length: id3v2Length,
        _probeContainer: probeContainer,
        __isStub: false,
    };
})();
